import { useState } from 'react'

import { Badge, usePriceInfoVariants } from '@/modules/Frontend/Theme'
import { PriceDisplay, formatPrice } from '@/modules/Frontend/UiBlocks'

import { PriceHint } from './PriceHint'

import type { PriceSummary } from '../../../lib/pricing'

function PriceWithVat({ price }: { price: number }) {
  const { priceInfoFinalPriceVariants, priceInfoVatVariants } = usePriceInfoVariants()

  return (
    <span className={priceInfoFinalPriceVariants()}>
      <PriceDisplay price={price} size="lg" />
      <span className={priceInfoVatVariants()}> TVA Inclus</span>
    </span>
  )
}

interface PriceLinesProps {
  price: PriceSummary
  /** The recommended retail price (PRP), null when the product has none */
  msrp: number | null
}

/** The price lines of the product view: the old price with its discount, or the PRP, above the price with VAT. */
export function PriceLines({ price, msrp }: PriceLinesProps) {
  const [showTooltip, setShowTooltip] = useState(false)
  const { priceInfoStrikethroughVariants, priceInfoMsrpVariants } = usePriceInfoVariants()
  const { regularPrice, finalPrice, discount, hasDiscount } = price
  const formattedMsrp = msrp === null ? null : formatPrice(msrp)

  return hasDiscount ? (
    <div>
      <div className="relative flex flex-row items-center gap-2">
        <PriceHint
          tooltipId="price-tooltip-discount"
          message={`Pretul recomandat de producator (PRP) pentru acest produs este: ${formattedMsrp}`}
          visible={showTooltip}
          onVisibleChange={setShowTooltip}
        />
        <span className={priceInfoStrikethroughVariants()}>
          {regularPrice > 0 ? formatPrice(regularPrice) : null}
        </span>
        <Badge variant="discount" className="text-base xl:text-xl mr-5">
          reducere - {discount}%
        </Badge>
      </div>
      <PriceWithVat price={finalPrice} />
    </div>
  ) : (
    <div className="flex flex-col items-start">
      {formattedMsrp && (
        <div className="relative flex flex-row items-center gap-2">
          <PriceHint
            tooltipId="price-tooltip-msrp"
            message="Acesta este pretul recomandat de producator (PRP)"
            visible={showTooltip}
            onVisibleChange={setShowTooltip}
          />
          <span className={priceInfoMsrpVariants()}>
            PRP: <span>{formattedMsrp}</span>
          </span>
        </div>
      )}
      <PriceWithVat price={regularPrice} />
    </div>
  )
}
